<template>
  <div>
    <page-header
      :actions="headerActionConfig"
      left-side-class="col-xl-6 pb-2"
      :page="headerConfig"
      right-side-class="col-xl-6 align-items-end d-flex pb-4 justify-content-end"
    />

    <add-addendum
      v-if="addingAddendum"
      @on-cancel="() => (this.addingAddendum = false)"
      @on-continue="onContinueAddAddendumHandler($event)"
    />
    <board v-if="inEditMode" class="mb-4" :details="board">
      <template #data-item="{ item }">
        <div v-if="item.statusName">
          <balloon
            :bg="`border-none text-center bg-${getStatusColor(item.statusId)}`"
            dot
            :text="item.statusName"
          />
        </div>
      </template>
    </board>
    <validation-observer ref="formValidator">
      <b-tabs
        v-model="tabModel"
        nav-class="custom-nav-container"
        :no-nav-style="true"
      >
        <b-tab
          id="information"
          class="bid-tabs"
          content-class="mt-3 pb-3"
          :lazy="false"
        >
          <template #title>
            <span :class="{ 'tab-title-error': tabErrorKey === 'information' }">
              Información del Pliego
              <i
                v-if="tabErrorKey === 'information'"
                class="fas fa-exclamation-triangle text-danger ml-1"
              />
            </span>
          </template>
          <template v-if="model.purchaseService">
            <section-title
              class="mt-4"
              content="Información de Compra de Servicio"
              icon="fas fa-info"
              reverse
            />
            <div class="row">
              <div class="col-12 col-md-6 col-lg-3  mb-3">
                <label class="data-label">
                  Fecha de Solicitud:
                </label>
                <div>
                  {{ model.purchaseService.date | date }}
                </div>
              </div>
              <div class="col-12 col-md-6 col-lg-3  mb-3">
                <label class="data-label">
                  Número de Solicitud:
                </label>
                <div>
                  {{ model.purchaseService.number | empty }}
                </div>
              </div>
              <div class="col-12 col-md-6 col-lg-3  mb-3">
                <label class="data-label">
                  Tipo de Contratación:
                </label>
                <div>
                  {{ model.purchaseService.contractType?.name | empty }}
                </div>
              </div>
              <div class="col-12 col-md-6 col-lg-3  mb-3">
                <label class="data-label">
                  Nombre del Solicitante:
                </label>
                <div>
                  {{ model.purchaseService.contactName | empty }}
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-12 col-md-6 col-lg-3  mb-3">
                <label class="data-label">
                  Oficina:
                </label>
                <div>
                  {{ model.purchaseService.contactOffice | empty }}
                </div>
              </div>
              <div class="col-12 col-md-6 col-lg-3  mb-3">
                <label class="data-label">
                  Teléfono:
                </label>
                <div>
                  {{ model.purchaseService.contactPhoneNumber | empty }}
                </div>
              </div>
              <div class="col-12 col-md-6 col-lg-3  mb-3">
                <label class="data-label">
                  Correo Electrónico:
                </label>
                <div>
                  {{ model.purchaseService.contactEmail | empty }}
                </div>
              </div>
            </div>
          </template>
          <section-title
            class="mt-4"
            content="Información del Pliego"
            icon="fas fa-info"
            reverse
          />
          <div class="row">
            <div class="col-12 mb-3">
              <validation-provider v-slot="{ errors }" rules="required">
                <custom-input
                  v-model="model.title"
                  custom-error-msg="Favor completar título."
                  :disabled="titleDisabled"
                  :error="errors[0]"
                  label="Título:"
                />
              </validation-provider>
            </div>
            <div class="col-12 col-md-4 mb-3">
              <validation-provider v-slot="{ errors }" rules="required">
                <custom-drop-down
                  v-model="model.area"
                  :clearable="!isAreaDisabled"
                  custom-error-msg="Favor seleccionar área."
                  :disabled="isAreaDisabled"
                  :error="errors[0]"
                  fieldkey="id"
                  fieldname="name"
                  label="Área:"
                  :options="areas"
                  placeholder="Seleccione"
                  @input="onChangeArea"
                />
              </validation-provider>
            </div>
            <div v-if="!isInfraBid" class="col-12 col-md-4 mb-3">
              <validation-provider v-slot="{ errors }">
                <custom-drop-down
                  v-model="model.service"
                  :clearable="!isServiceDisabled"
                  custom-error-msg="Favor seleccionar un servicio a ofrecer."
                  :disabled="isServiceDisabled"
                  :error="errors[0]"
                  fieldkey="id"
                  fieldname="name"
                  label="Servicio a Ofrecer:"
                  :options="services"
                  placeholder="Seleccione"
                />
              </validation-provider>
            </div>
          </div>
          <div class="row">
            <div v-if="!isReadonly" class="col-md-4 mb-3">
              <div class="d-flex flex-column">
                <custom-drop-down
                  v-model="providerTypeSelected"
                  :clearable="false"
                  fieldkey="id"
                  fieldname="name"
                  label="Tipo de Proveedor:"
                  :options="providerTypes"
                  placeholder="Seleccione"
                />
                <action-button
                  class="mt-3 align-end"
                  i="fas fa-file-plus"
                  variant="success"
                  @click="addProviderType()"
                >
                  Añadir
                </action-button>
              </div>
            </div>
            <div class="col-md-8 mb-3 border-left">
              <custom-chips-group
                class="mt-n3 chips-group-providers"
                :disabled="isReadonly"
                no-background
                no-wrap
                object-text="name"
                :on-item-deleted="onDeleteProviderTypeHandler"
                :selected-items="providerTypesAdded"
                title="Tipo(s) de Proveedor(es) Seleccionado(s):"
              />
            </div>
          </div>
          <div class="row">
            <div class="col-md-6 mb-3">
              <validation-provider v-slot="{ errors }" rules="required">
                <custom-drop-down
                  v-model="model.serviceType"
                  custom-error-msg="Favor seleccionar tipo de servicio."
                  :disabled="isReadonly"
                  :error="errors[0]"
                  fieldkey="id"
                  fieldname="name"
                  label="Tipo de Servicio:"
                  :options="serviceTypes"
                  placeholder="Seleccione"
                />
              </validation-provider>
            </div>
            <div class="col-md-6 mb-3">
              <validation-provider v-slot="{ errors }" rules="required">
                <custom-drop-down
                  v-model="model.region"
                  custom-error-msg="Favor seleccionar región."
                  :disabled="regionDisabled"
                  :error="errors[0]"
                  fieldkey="id"
                  fieldname="name"
                  label="Región:"
                  :options="regions"
                  placeholder="Seleccione"
                />
              </validation-provider>
            </div>
            <div class="col-md-4 mb-3">
              <validation-provider v-slot="{ errors }" rules="required">
                <custom-date-picker
                  v-model="model.publishDate"
                  class="custom-date-picker__extended"
                  custom-error-msg="Favor seleccionar fecha de publicación."
                  :disabled="isReadonly"
                  :error="errors[0]"
                  label="Fecha de Publicación:"
                  :min-date="minPublishDate"
                  placeholder="DD/MM/YYYY"
                  @input="model.startDate = null"
                />
              </validation-provider>
            </div>
            <div class="col-md-4 mb-3">
              <validation-provider v-slot="{ errors }" rules="required">
                <custom-date-picker
                  v-model="model.startDate"
                  class="custom-date-picker__extended"
                  custom-error-msg="Favor seleccionar fecha de comienzo."
                  :disabled="dateDisabled"
                  :error="errors[0]"
                  label="Fecha de Comienzo:"
                  :min-date="minStartDate"
                  placeholder="DD/MM/YYYY"
                  @input="resetDates"
                />
              </validation-provider>
            </div>
            <div class="col-md-4 mb-3">
              <div v-if="isReadonly && !model.preAuctionDate && dateDisabled">
                <label class="data-label">Fecha de Presubasta:</label>
                <div>-</div>
              </div>
              <custom-date-picker
                v-else
                v-model="model.preAuctionDate"
                class="custom-date-picker__extended"
                :disabled="dateDisabled"
                label="Fecha de Presubasta:"
                :max-date="model.endDate"
                :min-date="minPreAuctionDate"
                placeholder="DD/MM/YYYY"
              />
            </div>
            <div class="col-md-4 mb-3">
              <validation-provider v-slot="{ errors }" rules="required">
                <custom-date-picker
                  v-model="model.endDate"
                  class="custom-date-picker__extended"
                  custom-error-msg="Favor seleccionar fecha de finalización."
                  :disabled="dateDisabled"
                  :error="errors[0]"
                  label="Fecha de Finalización:"
                  :min-date="minEndDate"
                  placeholder="DD/MM/YYYY"
                  @input="resetDates"
                />
              </validation-provider>
            </div>
          </div>
          <div class="mt-2">
            <label class="data-label">Descripción del Anuncio:</label>
            <div v-if="descriptionDisabled" v-html="model.description" />
            <validation-provider
              v-else
              v-slot="{ errors, classes }"
              :custom-messages="{
                required: 'Favor completar descripción del anuncio',
              }"
              name="descriptionditor"
              rules="required"
            >
              <custom-editor
                v-model="model.description"
                :class="{ 'was-Invalidated': classes.invalid }"
                placeholder="Redacte una descripción"
              />
              <Error
                v-if="errors[0] && errors[0].length > 0"
                :message="errors[0]"
                variant="no-border"
              />
            </validation-provider>
          </div>

          <section-title
            class="section-title__extended mt-4"
            content="Configuración de Pliego"
            icon="fas fa-folder-open text-orange"
            reverse
          />
          <configuration
            v-model="model"
            :is-read-only="isReadonly"
            :is-submitting="isSubmitting"
            :services="services"
          />

          <section-title
            class="section-title__extended mt-4"
            content="Documentos Relacionados a la Solicitud de Propuestas"
            icon="fas fa-cloud-upload-alt text-orange"
            reverse
          />
          <bid-document
            :adding-addendum="documentsDisabled"
            :documents="model.documents"
            :read-only="isReadonly"
            @on-add-document="addDocumentHandler"
            @on-remove-document="removeDocumentHandler"
          />
          <section-title
            class="section-title__extended mt-4"
            content="Información de Contacto"
            icon="fas fa-user-circle text-orange"
            reverse
          />
          <div class="row">
            <div class="col-sm-12 col-md-6 mb-3">
              <validation-provider
                v-slot="{ errors }"
                :custom-messages="{
                  required: 'Favor completar teléfono de contacto.',
                  length: 'El teléfono es invalido',
                }"
                rules="required|length:13"
              >
                <custom-input
                  v-model="model.phone"
                  :disabled="contactDisabled"
                  :error="errors[0]"
                  label="Número de Teléfono:"
                  mask-type="Phone"
                  :masked="true"
                  placeholder=""
                />
              </validation-provider>
            </div>
            <div class="col-sm-12 col-md-6 mb-3">
              <validation-provider
                v-slot="{ errors }"
                :custom-messages="{
                  required: 'Favor completar correo electrónico.',
                  email: 'El correo electrónico no es válido.',
                }"
                :rules="'required|email'"
              >
                <custom-input
                  v-model="model.email"
                  :disabled="contactDisabled"
                  :error="errors[0]"
                  label="Correo Electrónico:"
                />
              </validation-provider>
            </div>
          </div>
        </b-tab>

        <b-tab
          v-if="model.id"
          id="proposalDocuments"
          class="bid-tabs"
          content-class="mt-3 pb-3"
          :lazy="false"
        >
          <template #title>
            <span
              :class="{
                'tab-title-error': tabErrorKey === 'proposalDocuments',
              }"
            >
              Documentos de la Propuesta
              <i
                v-if="tabErrorKey === 'proposalDocuments'"
                class="fas fa-exclamation-triangle text-danger ml-1"
              />
            </span>
          </template>
          <document-types
            :announcement-id="model.id"
            :read-only="isReadonly"
            @refresh-detail="refreshData()"
          />
        </b-tab>

        <b-tab
          v-if="model.determinationPurchaseServiceId"
          id="determinationDocuments"
          class="bid-tabs"
          content-class="mt-3 pb-3"
          :lazy="false"
        >
          <template #title>
            <span
              :class="{
                'tab-title-error': tabErrorKey === 'determinationDocuments',
              }"
            >
              Documentos de Solicitud
              <i
                v-if="tabErrorKey === 'determinationDocuments'"
                class="fas fa-exclamation-triangle text-danger ml-1"
              />
            </span>
          </template>
          <section-title
            class="section-title__extended mt-4"
            content="Documentos de Trabajo Relacionados con la Compra de Servicios"
            icon="fas fa-cloud-upload-alt text-orange"
            reverse
          />
          <determination-purchase-service-documents
            :id="model.determinationPurchaseServiceId"
            :is-read-only="isReadonly"
            :is-submitting="isSubmitting"
            :items="model.determinationPurchaseServiceDocuments"
          />
        </b-tab>

        <b-tab
          v-if="canSeeProponents"
          id="proponents"
          class="bid-tabs"
          content-class="mt-3 pb-3"
        >
          <template #title>
            <span :class="{ 'tab-title-error': tabErrorKey === 'proponents' }">
              Proponentes
              <i
                v-if="tabErrorKey === 'proponents'"
                class="fas fa-exclamation-triangle text-danger ml-1"
              />
            </span>
          </template>
          <proponents
            :id="model.determinationPurchaseServiceId"
            :determination-category-id="
              model.purchaseService?.determinationCategoryId
            "
            :estimated-amount="model.purchaseService.estimatedAmount"
            :is-read-only="isReadonly"
            :is-renewal-or-amendment="isRenewalOrAmendment"
            :items.sync="model.proponents"
            :minimum-proponents-required="minimumProponentsRequired"
          />
        </b-tab>

        <b-tab
          v-if="isReadonly"
          id="addendumHistory"
          class="bid-tabs"
          content-class="mt-3 pb-3"
          :lazy="false"
        >
          <template #title>
            <span
              :class="{ 'tab-title-error': tabErrorKey === 'addendumHistory' }"
            >
              Historial de Addendums
              <i
                v-if="tabErrorKey === 'addendumHistory'"
                class="fas fa-exclamation-triangle text-danger ml-1"
              />
            </span>
          </template>
          <addendum-history
            :history="model.addendumHistory"
            :status="model.status"
          />
        </b-tab>

        <b-tab
          v-if="model.proposalQuestionnaireRequired"
          id="proposalQuestionnaire"
          class="bid-tabs"
          content-class="mt-3 pb-3"
        >
          <template #title>
            <span
              :class="{
                'tab-title-error': tabErrorKey === 'proposalQuestionnaire',
              }"
            >
              Cuestionario de Propuesta
              <i
                v-if="tabErrorKey === 'proposalQuestionnaire'"
                class="fas fa-exclamation-triangle text-danger ml-1"
              />
            </span>
          </template>
          <proposal-questionnaire
            v-model="model.questionnaireConfigId"
            :area-id="model.area?.id"
            :is-read-only="isReadonly"
            :service-id="model.service?.id"
          >
            <template #questionnaire-error>
              <div v-if="tabErrorKey === 'proposalQuestionnaire'" class="mt-2">
                <Error
                  message="Favor seleccionar una configuración."
                  variant="no-border"
                />
              </div>
            </template>
          </proposal-questionnaire>
        </b-tab>

        <b-tab
          v-if="model.evaluationCommitteeRequired"
          id="evaluators"
          class="bid-tabs"
          content-class="mt-3 pb-3"
          :lazy="false"
        >
          <template #title>
            <span :class="{ 'tab-title-error': tabErrorKey === 'evaluators' }">
              Evaluadores
              <i
                v-if="tabErrorKey === 'evaluators'"
                class="fas fa-exclamation-triangle text-danger ml-1"
              />
            </span>
          </template>
          <evaluator
            v-model="model.assignedEvaluators"
            :evaluators="evaluators"
            :read-only="model.hasEvaluatorAssigned"
          />
        </b-tab>
      </b-tabs>
    </validation-observer>
  </div>
</template>
<script>
import PageHeader from 'PageHeader';
import SectionTitle from 'SectionTitle';
import CustomInput from 'CustomInput';
import CustomDatePicker from 'custom-date-picker';
import CustomDropDown from 'custom-drop-down';
import CustomEditor from '@quisit/custom-editor';
import BidDocument from './components/BidDocument';
import Error from 'custom-error';
import AddAddendum from './components/AddAddendum';
import CustomChipsGroup from 'CustomChipsGroup';
import Evaluator from './components/Evaluator.vue';
import Configuration from './components/Configuration.vue';
import DeterminationPurchaseServiceDocuments from './components/DeterminationPurchaseServiceDocuments.vue';
import Proponents from './components/Proponents.vue';
import ProposalQuestionnaire from './components/ProposalQuestionnaire.vue';
import {
  getInitialDataToCreate as _getInitialDataToCreate,
  saveAnnouncement as _saveAnnouncement,
  getAnnouncementDetail as _getAnnouncementDetail,
  submitAnnouncement as _submitAnnouncement,
  upsertEvaluators as _upsertEvaluators,
  sendInvitation as _sendInvitation,
} from '@/modules/proposalProcess/api.js';

import {
  getServiceTypesByArea,
  getAllEvaluatorUsers,
} from '@/modules/serviceProposalHistory/api';
import { getById } from '@/modules/purchaseService/api.js';
//utils
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import determinationCategories from '@/utils/constants/purchaseServiceDeterminationCategories';

import { serialize as objectToFormData } from 'object-to-formdata';
import AddendumHistory from './components/AddendumHistory.vue';
import DocumentTypes from './components/DocumentTypes.vue';

import {
  announcementStatuses,
  getStatusColor as _getStatusColor,
} from '@/utils/constants/announcementStatus.js';

import ServiceTypes from '@/utils/constants/proposalServiceTypes';
import PurchaseServiceContractType from '@/utils/constants/purchaseServiceContractType';

import Board from 'Board';
import ProposalAreas from '@/utils/constants/proposalAreas';
import moment from 'moment';
import proposalEvaluationTypes from '@/views/provider/proposalHistory/proposalEvaluationTypes';
import { getSysparameterByNames as _getSysparameterByNames } from '@/modules/shared/sysparameter.api';

const TABS = {
  INFORMATION: 0,
  PROPOSAL_DOCUMENTS: 1,
};

export default {
  name: 'BidDocumentationManagement',
  components: {
    PageHeader,
    SectionTitle,
    CustomInput,
    CustomDatePicker,
    CustomDropDown,
    CustomEditor,
    BidDocument,
    Error,
    AddAddendum,
    AddendumHistory,
    Board,
    CustomChipsGroup,
    Configuration,
    DocumentTypes,
    Evaluator,
    DeterminationPurchaseServiceDocuments,
    Proponents,
    ProposalQuestionnaire,
  },
  mixins: [alertMixin],
  data() {
    return {
      requiredEvaluators: 2,
      tabModel: null,
      tabErrorKey: null,
      isSubmitting: false,
      areas: [],
      serviceTypes: [],
      regions: [],
      providerTypes: [],
      providerTypesOriginal: [],
      providerTypesAdded: [],
      addingAddendum: false,
      isReadonly: false,
      titleDisabled: false,
      regionDisabled: false,
      dateDisabled: false,
      descriptionDisabled: false,
      documentsDisabled: false,
      contactDisabled: false,
      addendumModel: null,
      providerTypeSelected: null,
      services: [],
      evaluators: [],
      requiredOptions: [
        { name: 'answerYes', label: 'SI', value: true },
        { name: 'answerNo', label: 'NO', value: false },
      ],
      model: {
        id: null,
        title: null,
        description: null,
        area: null,
        serviceType: null,
        region: null,
        publishDate: null,
        startDate: null,
        preAuctionDate: null,
        endDate: null,
        documents: [],
        phone: null,
        email: null,
        status: null,
        addendumHistory: [],
        providerTypes: [],
        submitted: false,
        service: null,
        rubrics: [],
        minimumScore: null,
        assignedEvaluators: [],
        rubricRequired: null,
        evaluationCommitteeRequired: null,
        proposalQuestionnaireRequired: null,
        ssnRequired: null,
        questionnaireConfigId: null,
        servicesRenewableRequired: null,
        servicesRenewableDuration: null,
        servicesRenewableDurationType: null,
        blindEvaluationRequired: null,
        screeningProcessRequired: null,
        determinationPurchaseServiceId: null,
        determinationPurchaseService: null,
        proponents: [],
        purchaseService: null,
      },
    };
  },
  computed: {
    minimumProponentsRequired() {
      if (
        this.model.purchaseService?.determinationCategoryId !==
        determinationCategories.Quotation
      ) {
        return 1;
      }

      if (
        this.model.purchaseService?.estimatedAmount > 10000 &&
        this.model.purchaseService?.estimatedAmount <= 100000
      ) {
        return 3;
      }

      if (
        this.model.purchaseService?.estimatedAmount > 100000 &&
        this.model.purchaseService?.estimatedAmount < 250000
      ) {
        return 5;
      }

      return 1;
    },
    requiresSpecializedEvaluator() {
      return (
        this.model.evaluationCommitteeRequired &&
        Array.isArray(this.model.rubrics) &&
        this.model.rubrics.some((r) => r.isSpecialEvaluatorRequired)
      );
    },

    hasSpecializedEvaluator() {
      return (
        Array.isArray(this.model.assignedEvaluators) &&
        this.model.assignedEvaluators.some((e) => e.isSpecialized)
      );
    },
    isRenewalOrAmendment() {
      return [
        PurchaseServiceContractType.Amendment,
        PurchaseServiceContractType.Renewal,
      ].includes(this.model.determinationPurchaseService?.contractType?.id);
    },
    canSeeProponents() {
      const contractTypeId = this.model.purchaseService?.contractType?.id;

      switch (contractTypeId) {
        case PurchaseServiceContractType.New:
          return (
            this.model.purchaseService?.determinationCategoryId !=
            determinationCategories.RFP
          );

        case PurchaseServiceContractType.Renewal:
          return true;

        case PurchaseServiceContractType.Amendment:
          return true;

        default:
          return false;
      }
    },
    isInfraBid() {
      return this.model.area?.id === ProposalAreas.Infraestructura;
    },
    headerConfig() {
      return {
        title: 'Añadir Pliego',
        icon: 'fas fa-city',
        routes: [
          'Proceso de Competencias',
          'Documentación de Pliego',
          'Añadir Pliego',
        ],
      };
    },
    headerActionConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => {
            this.$router.go(-1);
          },
        },
        {
          text: 'Añadir Addendum',
          visible:
            (this.isPublished || this.isFinalized) && !this.addingAddendum,
          icon: 'fas fa-file-medical',
          variant: 'success',
          click: () => (this.addingAddendum = true),
        },
        {
          text: 'Guardar',
          icon: 'fas fa-save',
          visible: !this.isReadonly,
          variant: 'success',
          click: () => this.saveHandler(),
        },
        {
          text: 'Guardar',
          icon: 'fas fa-save',
          visible: this.isReadonly && this.addendumModel != null,
          variant: 'success',
          click: () => this.saveAddendumHandler(),
        },
        {
          text: 'Guardar',
          icon: 'fas fa-save',
          visible:
            !this.model.hasEvaluatorAssigned &&
            this.isReadonly &&
            this.addendumModel == null,
          variant: 'success',
          click: () => this.saveEvaluators(),
        },
        {
          text: 'Someter',
          icon: 'fas fa-flag-alt',
          visible:
            !this.isReadonly && this.model?.id > 0 && !this.model?.submitted,
          variant: 'success',
          click: () => this.submitHandler(),
        },
        {
          text: 'Enviar Invitación',
          icon: 'fas fa-save',
          visible: this.isPublished && this.canSeeProponents,
          variant: 'success',
          click: () => this.sendInvitation(),
        },
      ];
    },
    board() {
      return [
        [
          {
            label: 'Número de Aviso:',
            icon: 'fas fa-file-alt',
            data: this.model?.announcementNumber,
          },
          {
            label: 'Estatus:',
            icon: 'fas fa-file-alt',
            statusId: this.model?.status?.id,
            statusName: this.model?.status?.name,
          },
          {
            label: 'Creado por:',
            icon: 'fas fa-clipboard-user',
            data: this.model?.createdBy,
          },
          {
            label: 'Fecha de Creación:',
            icon: 'fas fa-calendar-edit',
            data: this.$formatDate(this.model?.createdDate),
          },
        ],
      ];
    },
    minPublishDate() {
      let date = new Date();
      date.setDate(date.getDate() - 1);
      return date;
    },
    minStartDate() {
      if (!this.model.publishDate) return;

      let date = new Date(this.model.publishDate);
      date.setDate(date.getDate() - 1);
      return date;
    },
    minPreAuctionDate() {
      if (!this.model.startDate) return;

      let date = new Date(this.model.startDate);
      date.setDate(date.getDate() - 1);
      return date;
    },
    minEndDate() {
      if (!this.model.startDate) return;

      let date = new Date(this.model.startDate);
      date.setDate(date.getDate() - 1);
      return date;
    },
    isPublished() {
      return this.model?.status?.id == announcementStatuses.Publicado;
    },
    isFinalized() {
      return this.model?.status?.id == announcementStatuses.Finalizado;
    },
    inEditMode() {
      return this.model.id;
    },
    determinationPurchaseServiceId() {
      return Number(this.$route.query['cp_ps']) || null;
    },
    isDetermitedByPurchaseService() {
      return (
        this.determinationPurchaseServiceId ||
        this.model.determinationPurchaseServiceId
      );
    },
    isAreaDisabled() {
      return this.isReadonly || !!this.isDetermitedByPurchaseService;
    },
    isServiceDisabled() {
      return [
        this.isReadonly,
        !this.model.area,
        !!this.isDetermitedByPurchaseService,
      ].includes(true);
    },
  },
  async mounted() {
    await this.getInitialDataToCreate();

    if (this.$route.params.id) {
      this.model.id = Number(this.$route.params.id);
      await this.getBidDetails();
    }

    await this.getDeterminationPurchaseService();

    const { data } = await _getSysparameterByNames({
      parameterNames: ['RequiredEvaluators'],
    });

    this.requiredEvaluators =
      data?.length > 0 && !isNaN(Number(data[0].staticValue))
        ? Number(data[0].staticValue)
        : 2;
  },
  methods: {
    validateSpecializedEvaluatorRequirement() {
      if (!this.model.evaluationCommitteeRequired) {
        return true;
      }

      const evaluators = this.model.assignedEvaluators ?? [];

      const regularCount = evaluators.filter((e) => !e.isSpecialized).length;

      if (this.requiresSpecializedEvaluator && !this.hasSpecializedEvaluator) {
        this.ShowToast(
          'Alerta',
          'Debe seleccionar al menos un evaluador especializado, según la configuración de las rúbricas.',
          'error'
        );
        return false;
      }

      if (regularCount < this.requiredEvaluators) {
        this.ShowToast(
          'Alerta',
          `Debe seleccionar al menos ${this.requiredEvaluators} evaluadores no especializados. El evaluador especializado no cuenta dentro de este mínimo.`,
          'error'
        );
        return false;
      }

      return true;
    },
    validateMinimumProponentsRequired() {
      if (!this.canSeeProponents) return true;

      const totalProponents = this.model.proponents?.length || 0;

      if (totalProponents < this.minimumProponentsRequired) {
        this.ShowToast(
          'Alerta',
          `Para este pliego es necesario agregar al menos ${this.minimumProponentsRequired} proponentes.`,
          'error'
        );
        return false;
      }

      return true;
    },
    async getDeterminationPurchaseService() {
      const id =
        this.model.determinationPurchaseServiceId ??
        this.determinationPurchaseServiceId;
      if (!id) return;
      const { data } = await getById(id, true);
      if (this.model.id) {
        this.model.determinationPurchaseService = data;
        return;
      }

      this.model.area = data.area;
      await this.onChangeArea(data.area);
      this.model.service = data.service;
      this.model.servicesRenewableRequired = data.canRenewal;
      if (data.canRenewal) {
        this.model.servicesRenewableDuration = data.renewalDuration;
        this.model.servicesRenewableDurationType = data.renewalDurationType;
      }
    },
    async getAllEvaluatorUsers(areaId) {
      const payload = { areaId };
      const { data } = await getAllEvaluatorUsers(payload);
      this.evaluators = data;
    },
    resetDates() {
      const startDate = this.model.startDate
        ? moment(this.model.startDate).toDate()
        : null;
      const preAuctionDate = this.model.preAuctionDate
        ? moment(this.model.preAuctionDate).toDate()
        : null;
      const endDate = this.model.endDate
        ? moment(this.model.endDate).toDate()
        : startDate;
      this.model.endDate = endDate >= startDate ? endDate : null;
      this.model.preAuctionDate =
        startDate <= preAuctionDate && preAuctionDate < endDate
          ? preAuctionDate
          : null;
    },
    async getInitialDataToCreate() {
      const id =
        this.model.determinationPurchaseServiceId ??
        this.determinationPurchaseServiceId;
      const params = id ? { checkPsAreasExcluded: true } : {};
      const { data } = await _getInitialDataToCreate(params);

      this.areas = data.areas;
      this.serviceTypes = data.serviceTypes;
      this.regions = data.regions;
      this.providerTypes = data.providerTypes;
      this.providerTypesOriginal = Array.from(data.providerTypes);
    },
    getStatusColor(statusId) {
      if (!statusId) return;

      return _getStatusColor(statusId);
    },
    async getBidDetails(clearCheck = false) {
      const { data } = await _getAnnouncementDetail(this.model.id);
      if (data.area) {
        await this.onChangeArea(data.area);
        await this.getAllEvaluatorUsers(data.area.id);
      }
      this.model = { ...this.model, ...data };

      this.providerTypesAdded = this.providerTypesOriginal.filter((c) =>
        data.providerTypes.includes(c.id)
      );

      this.providerTypes = this.providerTypesOriginal.filter(
        (c) => !data.providerTypes.includes(c.id)
      );

      if (clearCheck) {
        this.model.rubricRequired = null;
      }

      const statusForReadOnly = [
        announcementStatuses.Publicado,
        announcementStatuses.Finalizado,
      ];

      this.isReadonly = statusForReadOnly.includes(this.model.status.id);
      this.titleDisabled = this.regionDisabled = this.dateDisabled = this.descriptionDisabled = this.documentsDisabled = this.contactDisabled = this.isReadonly;
    },
    async refreshData() {
      await this.getBidDetails();
    },
    addDocumentHandler(document) {
      this.model.documents.push(document);
    },
    removeDocumentHandler(index) {
      if (index > -1) {
        this.model.documents.splice(index, 1);
      }
    },
    onDeleteProviderTypeHandler(elem) {
      const index = this.providerTypesAdded.findIndex((y) => y.id === elem.id);
      this.providerTypesAdded.splice(index, 1);

      this.providerTypes.push(elem);

      this.providerTypes.sort((a, b) => a.id - b.id);
    },
    async saveHandler(isAddendum = false, isSubmitting = false) {
      this.isSubmitting = isSubmitting;
      this.tabErrorKey = null;

      await this.$nextTick();

      let isValid = await this.$refs.formValidator.validate();

      if (this.model.rubricRequired && this.model.rubrics.length == 0) {
        this.ShowToast(
          'Alerta',
          'Favor seleccionar al menos 1 rúbrica.',
          'error'
        );
        return;
      }

      if (this.providerTypesAdded.length == 0) {
        this.ShowToast(
          'Alerta',
          'Favor seleccionar al menos 1 tipo de proveedor.',
          'error'
        );
        return;
      }

      if (this.model.rubricRequired) {
        if (
          this.model.service?.id === ServiceTypes.EducationalAndRelatedServices
        ) {
          const rubricServiceIds = this.model.rubrics.map((x) => x.serviceId);
          const requiredServiceTypes = [
            ServiceTypes.Educational,
            ServiceTypes.EvaluationAndOrTherapyRelatedServices,
            ServiceTypes.EducationalAndRelatedServices,
          ];

          const hasAllRequiredServiceTypes = requiredServiceTypes.every((x) =>
            rubricServiceIds.includes(x)
          );

          if (!hasAllRequiredServiceTypes) {
            this.ShowToast(
              'Alerta',
              'Es requerido un tipo de rúbrica de Educativos, Servicios relacionados de evaluación y/o terapias, y Educativos y Servicios relacionados.',
              'error'
            );
            return;
          }
        }

        const evaluationTypeIds = this.model.rubrics.map(
          (x) => x.evaluationTypeId
        );

        const requiredEvaluationTypes = {
          [ServiceTypes.Educational]: [
            proposalEvaluationTypes.PropuestaServiciosEducativasNueva,
            proposalEvaluationTypes.PropuestaServiciosEducativasRenovacion,
          ],
          [ServiceTypes.EvaluationAndOrTherapyRelatedServices]: [
            proposalEvaluationTypes.RelacionadosPropuestasNueva,
            proposalEvaluationTypes.RelacionadosPropuestasRenovacion,
          ],
          [ServiceTypes.EducationalAndRelatedServices]: [
            proposalEvaluationTypes.PropuestaServiciosEducativasNueva,
            proposalEvaluationTypes.PropuestaServiciosEducativasRenovacion,
            proposalEvaluationTypes.RelacionadosPropuestasNueva,
            proposalEvaluationTypes.RelacionadosPropuestasRenovacion,
            proposalEvaluationTypes.EducativosServiciosRelacionadosNueva,
            proposalEvaluationTypes.EducativosServiciosRelacionadosRenovacion,
          ],
        };

        const requiredServiceTypes =
          requiredEvaluationTypes[this.model.service.id] || [];

        const hasAllRequiredTypes = requiredServiceTypes.every((type) =>
          evaluationTypeIds.includes(type)
        );

        if (!hasAllRequiredTypes && this.model.rubricRequired) {
          this.ShowToast(
            'Alerta',
            'Es requerido una rúbrica de cada tipo de evaluación (Nueva, Renovación).',
            'error'
          );
          return;
        }
      }

      if (isSubmitting && !this.validateMinimumProponentsRequired()) {
        const element = this.$el.querySelector('#proponents');

        const panes = Array.from(this.$el.querySelectorAll('.bid-tabs'));
        const tabIndex = panes.indexOf(element);

        if (tabIndex > -1) {
          this.tabModel = tabIndex;
        }

        this.tabErrorKey = 'proponents';

        return;
      }

      if (
        isSubmitting &&
        this.model.proposalQuestionnaireRequired &&
        !this.model.questionnaireConfigId
      ) {
        const element = this.$el.querySelector('#proposalQuestionnaire');

        const panes = Array.from(this.$el.querySelectorAll('.bid-tabs'));
        const tabIndex = panes.indexOf(element);

        if (tabIndex > -1) {
          this.tabModel = tabIndex;
        }

        this.tabErrorKey = 'proposalQuestionnaire';

        this.ShowToast(
          'Alerta',
          'Es querido seleccionar un cuestionario para el pliego.',
          'error'
        );
        return;
      }

      if (
        (isSubmitting || this.model?.submitted) &&
        !this.validateSpecializedEvaluatorRequirement()
      ) {
        const element = this.$el.querySelector('#evaluators');

        const panes = Array.from(this.$el.querySelectorAll('.bid-tabs'));
        const tabIndex = panes.indexOf(element);

        if (tabIndex > -1) {
          this.tabModel = tabIndex;
        }

        this.tabErrorKey = 'evaluators';

        return;
      }

      if (!isValid) {
        this.ShowToast(
          'Alerta',
          'Favor completar la información solicitada.',
          'error'
        );

        await this.showTabWithError();

        return false;
      }

      try {
        let params = {
          id: this.model.id,
          areaId: this.model.area?.id,
          serviceId: this.model.service?.id,
          serviceTypeId: this.model.serviceType?.id,
          regionId: this.model.region?.id,
          minimumScore: this.model.minimumScore,
          title: this.model.title,
          description: this.model.description,
          publishDate: this.model.publishDate,
          startDate: this.model.startDate,
          endDate: this.model.endDate,
          preAuctionDate: this.model.preAuctionDate,
          phone: this.model.phone,
          email: this.model.email,
          providerTypes: this.providerTypesAdded?.map((c) => c.id) ?? [],
          rubricRequired: this.model.rubricRequired,
          rubrics: this.model.rubricRequired
            ? this.model.rubrics?.map((x) => x.id) ?? []
            : [],
          evaluationCommitteeRequired: this.model.evaluationCommitteeRequired,
          proposalQuestionnaireRequired: this.model
            .proposalQuestionnaireRequired,
          questionnaireConfigId: this.model.proposalQuestionnaireRequired
            ? this.model.questionnaireConfigId
            : null,
          ssnRequired: this.model.ssnRequired,
          servicesRenewableRequired: this.model.servicesRenewableRequired,
          servicesRenewableDuration: this.model.servicesRenewableDuration,
          servicesRenewableDurationType: this.model
            .servicesRenewableDurationType,
          blindEvaluationRequired: this.model.blindEvaluationRequired,
          screeningProcessRequired: this.model.screeningProcessRequired,
          assignedEvaluators: this.model.evaluationCommitteeRequired
            ? this.model.assignedEvaluators
            : [],
          determinationPurchaseServiceId: this.determinationPurchaseServiceId,
          proponents: this.canSeeProponents ? this.model.proponents : [],
          purchaseServiceId: this.model.determinationPurchaseService?.id,
        };

        if (isAddendum) {
          params = {
            ...params,
            addendum: {
              reasons: this.addendumModel.addendumReasons
                .map((c) => c.name)
                .join(', '),
              comment: this.addendumModel.comment,
            },
          };
          this.addendumModel = null;
          this.addingAddendum = false;
        }

        let formData = objectToFormData(params, {
          indices: true,
          nullsAsUndefineds: true,
        });

        if (this.model.documents.length) {
          this.model.documents.forEach((document, index) => {
            formData.append(`documents[${index}].file`, document.file);
            formData.append(
              `documents[${index}].documentName`,
              document.documentName
            );
            if (document.addendumId) {
              formData.append(
                `documents[${index}].addendumId`,
                document.addendumId
              );
            }
            if (document.documentId) {
              formData.append(
                `documents[${index}].documentId`,
                document.documentId
              );
            }
          });
        }
        const clearCheck =
          !this.model.id && this.model.rubricRequired === null ? true : false;

        const { data: announcementId } = await _saveAnnouncement(formData);

        this.model.id = announcementId;

        this.ShowToast(
          'Proceso Exitoso',
          isAddendum
            ? 'Se guardó addendum exitosamente.'
            : 'El pliego ha sido guardado exitosamente.',
          'success'
        );
        this.tabErrorKey = null;
        this.$router.replace(
          {
            name: 'BidDocumentationEdit',
            params: { id: announcementId },
          },
          this.getBidDetails(clearCheck)
        );

        await this.getDeterminationPurchaseService();

        return true;
      } catch {
        this.ShowToast('', 'Error al intentar guardar el pliego.', 'error');
        return false;
      }
    },
    async showTabWithError() {
      // Intentar identificar la primera entrada inválida y cambiar a la pestaña correspondiente
      await this.$nextTick();
      const invalidEl = this.$el.querySelector(
        '.was-Invalidated, .is-invalid, .invalid, [aria-invalid="true"]'
      );
      if (!invalidEl) return;

      // Buscar el panel de tab que contiene el elemento inválido.
      const pane = invalidEl.closest('[role="tabpanel"], .tab-pane');
      if (!pane) return;

      // marcar la pestaña con error por su id (si existe)
      if (pane.id) {
        this.tabErrorKey = pane.id;
      }

      const panes = Array.from(this.$el.querySelectorAll('.bid-tabs'));
      const tabIndex = panes.indexOf(pane);
      if (tabIndex > -1) {
        this.tabModel = tabIndex;
      }
    },
    onContinueAddAddendumHandler(data) {
      this.addendumModel = data;

      data.addendumReasons.forEach((x) => {
        switch (x.id) {
          case 'title':
            this.titleDisabled = false;
            break;
          case 'region':
            this.regionDisabled = false;
            break;
          case 'dates':
            this.dateDisabled = false;
            break;
          case 'description':
            this.descriptionDisabled = false;
            break;
          case 'documents':
            this.documentsDisabled = false;
            break;
          case 'contact':
            this.contactDisabled = false;
            break;
        }
      });
    },
    async saveAddendumHandler() {
      let confirmation = await this.ShowCancelModal(
        'Atención',
        `¿Esta seguro que desea guardar y publicar addendum?`,
        'Si',
        'No'
      );

      if (!confirmation) return;

      await this.saveHandler(true);
    },
    async saveEvaluators() {
      if (!this.validateSpecializedEvaluatorRequirement()) return;

      let confirmation = await this.ShowCancelModal(
        'Atención',
        `¿Está seguro que desea guardar los evaluadores?`,
        'Si',
        'No'
      );

      if (!confirmation) return;

      try {
        await _upsertEvaluators({
          announcementId: this.model.id,
          evaluators: this.model.assignedEvaluators ?? [],
        });

        this.ShowToast(
          'Proceso Exitoso',
          'Los evaluadores fueron guardados exitosamente.',
          'success'
        );
      } catch {
        this.ShowToast(
          '',
          'Error al intentar guardar los evaluadores.',
          'error'
        );
      }
    },

    async submitHandler() {
      const isValid = await this.saveHandler(false, true);

      if (!isValid) {
        return;
      }

      if (!this.model?.hasDocumentsForProposal) {
        this.ShowToast(
          'Atención',
          'Antes de someter es requerido definir los documentos para las propuestas.',
          'error'
        );
        this.tabModel = TABS.PROPOSAL_DOCUMENTS;
        this.tabErrorKey = 'proposalDocuments';
        return;
      }

      let confirmation = await this.ShowCancelModal(
        'Atención',
        `¿Esta seguro que desea Someter pliego?`,
        'Si',
        'No'
      );

      if (!confirmation) return;

      await _submitAnnouncement(this.model.id);

      this.ShowToast(
        'Proceso Exitoso',
        'Pliego fue sometido exitosamente y será visible a partir de la fecha de publicación.',
        'success'
      );

      await this.getBidDetails();
    },
    addProviderType() {
      if (!this.providerTypeSelected) return;

      this.providerTypesAdded.push(this.providerTypeSelected);
      const index = this.providerTypes.findIndex(
        (y) => y.id === this.providerTypeSelected.id
      );
      this.providerTypes.splice(index, 1);
      this.providerTypeSelected = null;
    },
    async onChangeArea(item) {
      this.model.service = null;
      this.model.assignedEvaluators = [];
      this.services = [];
      this.evaluators = [];
      if (!item) return;
      const { data } = await getServiceTypesByArea(item.id);
      await this.getAllEvaluatorUsers(item.id);
      this.services = data;
    },
    async sendInvitation() {
      await _sendInvitation(this.model.id);

      this.ShowToast(
        'Proceso Exitoso',
        'Invitaciones enviadas con éxito.',
        'success'
      );
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/shared/scss/config/colors.scss';

.custom-date-picker__extended ::v-deep {
  .width-container {
    width: 100% !important;
  }
}

.section-title__extended ::v-deep {
  .dot {
    background-color: #fff !important;
  }
}

::v-deep {
  .input-display {
    margin-left: 0 !important;
  }
}

::v-deep {
  .nav-item {
    a {
      font-size: 18px !important;
    }
  }
}
.align-end {
  align-self: flex-end;
}
.chips-group-providers ::v-deep .bold {
  font-weight: 400 !important;
}

::v-deep .custom-nav-container {
  border-bottom: 1px solid map-get($colors, 'gray-5');
  .nav-item {
    .nav-link {
      font-size: 17.602px;
      font-weight: 700;
      color: map-get($colors, 'secondary-6');
      &.active {
        border-bottom: 3px solid map-get($colors, 'green');
        color: map-get($colors, 'green');
      }
      &.active:after {
        content: unset;
      }
    }
  }
}

i.fas.fa-exclamation-triangle {
  color: #ff5b5c !important;
}

::v-deep .custom-nav-container {
  .nav-item {
    .nav-link {
      .tab-title-error {
        color: #ff5b5c !important;
        display: inline-block;
        padding-bottom: 4px;
      }
    }
  }
}

::v-deep .custom-nav-container {
  .nav-item {
    .nav-link.active:has(.tab-title-error) {
      border-bottom: 3px solid #ff5b5c;
    }
  }
}
</style>
